import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Device } from "@capacitor/device";
import { auth } from "../lib/firebase/config";
import { authAPI, RegisterResponse } from "../lib/api/auth";
import { socialAuthAPI } from "../lib/api/social-auth";
import { User } from "../types";
import { storage } from "../utils/storage";

const USER_STORAGE_KEY = "app_user_data";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string, acceptedTerms: boolean) => Promise<RegisterResponse>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  logout: () => Promise<void>;
  checkEmailVerification: (email: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isNative, setIsNative] = useState<boolean | null>(null);

  // Check if running on native platform
  useEffect(() => {
    const checkPlatform = async () => {
      try {
        const info = await Device.getInfo();
        setIsNative(info.platform === "ios" || info.platform === "android");
      } catch {
        setIsNative(false);
      }
    };
    checkPlatform();
  }, []);

  // Initialize user state from storage
  useEffect(() => {
    const initUser = async () => {
      try {
        const storedUser = await storage.getItem(USER_STORAGE_KEY);
        if (storedUser) {
          console.log("Stored user found:", storedUser);
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error('Error initializing user:', error);
      }
    };
    initUser();
  }, []);

  const updateUser = async (userData: User | null) => {
    setUser(userData);
    try {
      if (userData) {
        await storage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
      } else {
        await storage.removeItem(USER_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error updating user in storage:', error);
    }
  };

  useEffect(() => {
    if (isNative === null) return; // Wait until platform check is complete

    let isMounted = true;
    let unsubscribe: (() => void) | undefined;

    const setupAuthStateListener = async () => {
      if (isNative) {
        // Setup native auth state listener
        FirebaseAuthentication.addListener('authStateChange', async (change) => {
          try {
            if (change.user && isMounted) {
              try {
                // Try to get user data
                const userData = await authAPI.getMe();
                await updateUser(userData);
              } catch (err) {
                // If getMe fails, log out the user
                console.error("Failed to get user data:", err);
                await logout();
              }
            } else if (!change.user && isMounted) {
              await updateUser(null);
            }
          } catch (err) {
            console.error("Auth state change error:", err);
            if (isMounted) {
              setError(err instanceof Error ? err.message : "Authentication error");
              // Also log out on general auth error
              await logout();
            }
          } finally {
            if (isMounted) {
              setIsLoading(false);
              setIsInitialized(true);
            }
          }
        });

        // Return cleanup function for native listener
        return () => {
          FirebaseAuthentication.removeAllListeners();
        };
      } else {
        // Setup web auth state listener
        unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          try {
            if (firebaseUser && isMounted) {
              try {
                // Try to get user data
                const userData = await authAPI.getMe();
                await updateUser(userData);
              } catch (err) {
                // If getMe fails, log out the user
                console.error("Failed to get user data:", err);
                await logout();
              }
            } else if (!firebaseUser && isMounted) {
              await updateUser(null);
            }
          } catch (err) {
            console.error("Auth state change error:", err);
            if (isMounted) {
              setError(err instanceof Error ? err.message : "Authentication error");
              await logout();
            }
          } finally {
            if (isMounted) {
              setIsLoading(false);
              setIsInitialized(true);
            }
          }
        });
      }
    };

    setupAuthStateListener();

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
      if (isNative) {
        FirebaseAuthentication.removeAllListeners();
      }
    };
  }, [isNative]);

  // Rest of the functions remain the same
  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const userData = await authAPI.login(email, password);
      await updateUser(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, name: string, acceptedTerms: boolean) => {
    try {
      setIsLoading(true);
      setError(null);
      const registerResponse = await authAPI.register(email, password, name, acceptedTerms);

      if (!registerResponse.success) {
        setError(registerResponse.error || "Registration failed");
      }

      return registerResponse;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const userData = await socialAuthAPI.loginWithGoogle();
      await updateUser(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Google login failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithApple = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const userData = await socialAuthAPI.loginWithApple();
      await updateUser(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Apple login failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (isNative) {
        await FirebaseAuthentication.signOut();
      } else {
        await auth.signOut();
      }

      try {
        await authAPI.logout();
      } catch (logoutErr) {
        console.error("Error during API logout:", logoutErr);
      }

      await updateUser(null);
    } catch (err) {
      console.error("Logout error:", err);
      setError(err instanceof Error ? err.message : "Logout failed");

      await updateUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const checkEmailVerification = async (email: string) => {
    try {
      console.log("Checking email verification for:", email);
      const verifyEmail = await authAPI.isEmailVerified(email);
      console.log("Email verification status:", verifyEmail);

      if (!verifyEmail) {
        setError("Email not verified");
      }

      return verifyEmail;
    } catch (err) {
      console.error("Error checking email verification:", err);
      return false;
    }
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        setUser: updateUser,
        login,
        register,
        loginWithGoogle,
        loginWithApple,
        logout,
        checkEmailVerification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}